<wr-base-dialog status="info"
                dialogWidth="large"
                cancelText="Schließen"
                submitText="Speichern"
                [header]="header()"
                [showSubmitButton]="canEdit()"
                [disableSubmit]="loading() || busy() || form.invalid"
                [cancelResponse]="cancelResponse"
                [submitResponse]="submitResponse">
  <div class="repair-task-edit" [nbSpinner]="loading() || busy()" data-cy="repair-task-edit-dialog">
    @if (!loading()) {
      @if (canEdit()) {
        <form [formGroup]="form" data-cy="repair-task-edit-form">
          <hau-form-field label="Titel">
            <input nbInput fullWidth formControlName="title" data-cy="repair-task-edit-title">
          </hau-form-field>
          <hau-form-field label="Beschreibung">
            <textarea nbInput fullWidth rows="5" formControlName="description"
                      data-cy="repair-task-edit-description"></textarea>
          </hau-form-field>
          <div class="field-row">
            <hau-form-field label="Frist">
              <input nbInput fullWidth placeholder="Keine Frist" formControlName="deadline"
                     [nbDatepicker]="deadlinePicker" data-cy="repair-task-edit-deadline">
              <nb-datepicker #deadlinePicker></nb-datepicker>
            </hau-form-field>
            <hau-form-field label="Status">
              <nb-select fullWidth formControlName="status" [placeholder]="statusPlaceholder()"
                         data-cy="repair-task-edit-status">
                @for (status of selectableStatuses; track status) {
                  <nb-option [value]="status">{{ status | repairTaskStatusTranslation }}</nb-option>
                }
              </nb-select>
            </hau-form-field>
          </div>
          <hau-form-field label="Dienstleister">
            <div class="contractor-field">
              @if (contractorOptions().length) {
                <nb-select fullWidth placeholder="Dienstleister auswählen" formControlName="contractorId"
                           data-cy="repair-task-edit-contractor">
                  @for (option of contractorOptions(); track option.id) {
                    <nb-option [value]="option.id">{{ option.name }}</nb-option>
                  }
                </nb-select>
              } @else {
                <wr-banner status="warning" [icon]="{ icon: 'alert-triangle-outline', pack: 'eva' }"
                           message="Dem Gebäude ist kein passender Dienstleister zugewiesen."></wr-banner>
              }
              @if (buildingId(); as buildingId) {
                <wr-repair-task-contractor-hint [buildingId]="buildingId"></wr-repair-task-contractor-hint>
              }
            </div>
          </hau-form-field>
        </form>
      } @else {
        @if (repairTask(); as task) {
          <dl class="facts" data-cy="repair-task-edit-readonly">
            <dt>Titel</dt>
            <dd data-cy="repair-task-edit-readonly-title">{{ task.title }}</dd>
            <dt>Beschreibung</dt>
            <dd class="multiline">{{ task.description || '–' }}</dd>
            <dt>Frist</dt>
            <dd>{{ task.deadline ? (task.deadline | date: 'dd.MM.yyyy') : '–' }}</dd>
            <dt>Dienstleister</dt>
            <dd>{{ task.contractor?.name ?? '–' }}</dd>
            <dt>Status</dt>
            <dd><wr-repair-task-status-text data-cy="repair-task-edit-readonly-status" [status]="task.status"></wr-repair-task-status-text></dd>
          </dl>
        }
      }

      @if (repairTask(); as task) {
        <dl class="facts">
          @if (task.building) {
            <dt>Gebäude</dt>
            <dd>{{ task.building.name }} ({{ task.building.internalNumber }})</dd>
          }
          @if (task.inspectionEntryCheckpoint) {
            <dt>Prüfpunkt</dt>
            <dd>{{ task.inspectionEntryCheckpoint.name }}</dd>
          }
          @if (task.buildingEquipment) {
            <dt>TGA</dt>
            <dd>{{ task.buildingEquipment.equipmentName }}</dd>
          }
        </dl>

        <h6>Verlauf</h6>
        <dl class="facts" data-cy="repair-task-edit-history">
          <dt>Erstellt am</dt>
          <dd>
            {{ task.created | date: 'dd.MM.yyyy HH:mm' }}
            @if (task.creatorName) {
              durch {{ task.creatorName }}
            }
          </dd>
          <dt>Versendet am</dt>
          <dd data-cy="repair-task-edit-sent-at">{{ task.sentAt ? (task.sentAt | date: 'dd.MM.yyyy HH:mm') : '–' }}</dd>
          @if (task.status === 'FINISHED') {
            <dt>Abgeschlossen am</dt>
            <dd>{{ task.finishedAt ? (task.finishedAt | date: 'dd.MM.yyyy HH:mm') : '–' }}</dd>
          }
          @if (task.status === 'DECLINED') {
            <dt>Storniert am</dt>
            <dd>{{ task.declinedAt ? (task.declinedAt | date: 'dd.MM.yyyy HH:mm') : '–' }}</dd>
          }
          @if (task.closedByName) {
            <dt>Durch</dt>
            <dd>{{ task.closedByName }}</dd>
          }
          @if (task.completionComment || task.declineComment) {
            <dt>Kommentar</dt>
            <dd class="multiline" data-cy="repair-task-edit-close-comment">
              {{ task.completionComment ?? task.declineComment }}
            </dd>
          }
        </dl>
        @if (task.completionAttachments.length > 0) {
          <h6>Abschlussfotos</h6>
          <wr-attachments-carousel [attachments]="task.completionAttachments"></wr-attachments-carousel>
        }

        @if (canEdit() || canFinish() || canDecline()) {
          <div class="actions" data-cy="repair-task-edit-actions">
            @if (canFinish()) {
              <button nbButton outline size="small" status="success" type="button" [disabled]="busy()"
                      (click)="close('finish')" data-cy="repair-task-edit-finish">
                <nb-icon icon="checkmark-circle-2-outline" pack="eva"></nb-icon>
                Abschließen
              </button>
            }
            @if (canDecline()) {
              <button nbButton outline size="small" status="warning" type="button" [disabled]="busy()"
                      (click)="close('decline')" data-cy="repair-task-edit-decline">
                <nb-icon icon="slash-outline" pack="eva"></nb-icon>
                Stornieren
              </button>
            }
            @if (canEdit() && (task.contractor || form.controls.contractorId.value)) {
              <button nbButton outline size="small" status="info" type="button"
                      [disabled]="busy() || form.invalid"
                      (click)="resend()" data-cy="repair-task-edit-resend">
                <nb-icon icon="paper-plane-outline" pack="eva"></nb-icon>
                {{ task.sentAt ? 'Erneut senden' : 'An Dienstleister senden' }}
              </button>
            }
            @if (canEdit()) {
              <button nbButton outline size="small" status="danger" type="button" [disabled]="busy()"
                      (click)="delete()" data-cy="repair-task-edit-delete">
                <nb-icon icon="trash-2-outline" pack="eva"></nb-icon>
                Löschen
              </button>
            }
          </div>
        }
      }
    }
  </div>
</wr-base-dialog>
